<script lang="ts">
  // libs
  import { onMount, onDestroy } from "svelte";
  import {
    convertToFormattedLocaleDate,
    convertISODateStringToTimeZoneSafeFormattedLocaleDate,
  } from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";
  import Flash from "@shared/services/flashv2.js";
  import { humanizeList } from "@retailer/helpers";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  // components
  import OutstandingInvoiceTable from "./OutstandingInvoiceTable.svelte";
  import PaidInvoiceTable from "./PaidInvoiceTable.svelte";
  import DatePicker from "@shared/svelte/Components/DatePicker.svelte";
  import InvoicesBannerSelector from "@retailer/svelte/Payments/Banners/selectors/InvoicesBannerSelector.svelte";
  import PaginationLarge from "@shared/svelte/Components/Pagination/Large.svelte";
  import PayWithCreditsModal from "@retailer/svelte/Payments/PayWithCreditsModal.svelte";
  import MultiSearchAndSelectDropdown from "@shared/svelte/Components/MultiSearchAndSelectDropdown.svelte";

  // types
  import { InvoicesStatuses, InvoiceTypes } from "@shared/types/Invoice";
  import type { Invoice } from "@shared/types/Invoice";
  import type { PaymentInstrument } from "@shared/types/PaymentInstrument";

  // services
  import * as UserNotificationsService from "@retailer/services/user_notifications_service";
  import * as SuspensionService from "@retailer/services/suspension_service";
  import * as InvoiceService from "@retailer/services/invoice_service";
  import * as PaymentsService from "@retailer/services/payments_service";
  import * as DwollaIdentityService from "@retailer/services/dwolla_identity_service";

  // stores
  import { PaymentsStore } from "@retailer/stores/PaymentsStore";
  import { SuspensionStore } from "@retailer/stores/SuspensionStore";
  import {
    resetInvoiceStore,
    resetInvoices,
    setCurrentTab,
    updateSearchValue,
    InvoiceStore,
  } from "@retailer/stores/InvoiceStore";
  import { userDisabled, dwollaIdentityStore } from "@retailer/svelte/stores/DwollaIdentityStore";
  import { userNotifications as UserNotificationsStore } from "@retailer/stores/UserNotificationsStore";

  // data
  let searchValue: string;
  let selectedInvoices: Invoice[] = [];
  let paymentAmount: number;
  let shouldShowPayInvoicesModal = false;
  let buttonLabel = "All Paid Dates";
  let distributorSearchValue = "";
  let selectedDistributors = [];
  let selectedOrderStates = [];

  export let distributors = [];

  const statusFilterOptions: { option: string; value: string | null }[] = [
    {
      option: "All Statuses",
      value: null,
    },
    {
      option: "Paid",
      value: "payment_settled",
    },
    {
      option: "Pending",
      value: "pending",
    },
    {
      option: "Closed",
      value: "closed",
    },
  ];
  const invoiceFilterOptions: { option: string; value: InvoiceTypes | null }[] = [
    {
      option: "All Invoices",
      value: null,
    },
    {
      option: "Invoice Payments",
      value: InvoiceTypes.INVOICE,
    },
  ];
  let statusFilter: string;
  let scheduledDate: string = convertToFormattedLocaleDate(new Date());

  // functions
  // ---------
  // pay invoices modal logic
  function togglePayInvoicesModal() {
    shouldShowPayInvoicesModal = !shouldShowPayInvoicesModal;
  }

  const paySelected = async () => {
    const total = selectedInvoices.reduce((acc, cur) => acc + Number(cur.amount_due), 0);
    paymentAmount = total;
    togglePayInvoicesModal();
  };

  // invoice logic
  const viewInvoice = (invoiceId: number) =>
    window["stateService"].go("layout.invoice_details", { invoiceId });

  const cancelScheduledPayment = (invoiceId: number) => {
    Flash.now({ type: "success", message: "Your scheduled payment was successfully canceled." });

    PaymentsService.cancel([invoiceId], cancelSuccessCallback, cancelErrorCallback);
  };

  const handleScheduledPaymentModification = (invoice: Invoice) => {
    // Check the checkbox for the invoice
    const invoiceCheckbox = document.getElementById(`checkbox-${invoice.id}`) as HTMLInputElement;
    invoiceCheckbox.checked = true;

    if (!selectedInvoices.includes(invoice)) {
      selectedInvoices.push(invoice);
      selectedInvoices = selectedInvoices;
    }

    if (invoice.payment_scheduled_for) {
      scheduledDate = convertISODateStringToTimeZoneSafeFormattedLocaleDate(
        invoice.payment_scheduled_for,
      );
    } else {
      scheduledDate = convertToFormattedLocaleDate(new Date());
    }
    paySelected();
  };

  const cancelSuccessCallback = async () => {
    // reset invoice table
    resetInvoices();

    // fetch new invoices
    await InvoiceService.index(getCurrentStatus(), searchValue, $InvoiceStore.filters, pageNum);
    PaymentsStore.set({ loaded: true, failed: false });
  };
  const cancelErrorCallback = res => {
    PaymentsStore.set({ loaded: true, failed: true, errors: res["errors"][0]["error"] });
    Flash.now({ type: "error", message: $PaymentsStore.errors });
  };
  const fetchInvoices = async (page?: number) => {
    // reset invoice table
    resetInvoices();

    // fetch new invoices
    await InvoiceService.index(
      getCurrentStatus(),
      searchValue,
      $InvoiceStore.filters,
      Number(page || 1),
    );
  };

  const handleTabToggle = async (newTab: string, refresh: boolean = true) => {
    // dont do anything if same tab
    if ($InvoiceStore.currentTab == newTab) return;

    // set searchValue
    searchValue = $InvoiceStore.searchValues[newTab];

    // set current tab
    setCurrentTab(newTab);

    // clear selected invoices
    selectedInvoices = [];

    // fetch new invoices
    if (refresh) {
      await fetchInvoices();
    }
  };

  const getCurrentStatus = () => {
    return $InvoiceStore.currentTab == InvoicesStatuses.OUTSTANDING
      ? InvoicesStatuses.OUTSTANDING
      : statusFilter || $InvoiceStore.currentTab;
  };

  // payment logic
  async function onSchedulePayment(
    selectedPaymentMethod: PaymentInstrument,
    chosenDate: string,
    selectedDistributorIds: number[],
    selectedCredits: { [k: number]: number[] } = {},
  ) {
    await PaymentsService.schedulePayments(
      selectedPaymentMethod.id,
      chosenDate,
      selectedInvoices.map(a => a.id),
      scheduledPaymentSuccessCallback,
      scheduledPaymentErrorCallback,
      selectedDistributorIds,
      selectedCredits,
    );
    await fetchInvoices(pageNum);
    await InvoiceService.counts();

    // close modal
    togglePayInvoicesModal();

    scheduledDate = convertToFormattedLocaleDate(new Date());
  }

  const paymentSuccessTab = () => {
    if (scheduledDate == convertToFormattedLocaleDate(new Date())) {
      return InvoicesStatuses.PAID;
    } else {
      return InvoicesStatuses.OUTSTANDING;
    }
  };

  const scheduledPaymentSuccessCallback = (futureDate: boolean) => {
    const msg = futureDate ? "Your payment has been scheduled." : "Your payment was submitted.";
    Flash.now({ type: "success", message: msg });
    // reset selectedInvoices
    selectedInvoices = [];

    setCurrentTab(paymentSuccessTab());
  };

  const scheduledPaymentErrorCallback = () => {
    resetInvoices();
    selectedInvoices = [];

    let failedReferenceIDs = $PaymentsStore.errors.map(error => "#" + error.invoice_reference_id);
    Flash.now({
      type: "error",
      message:
        "There was an error submitting payment for " +
        pluralize("invoice", failedReferenceIDs.length, false) +
        " " +
        humanizeList("and")(failedReferenceIDs) +
        ", please contact us for help.",
    });

    setCurrentTab(InvoicesStatuses.OUTSTANDING);
  };

  const handleSort = async (sort: { order: string; direction: string }) => {
    InvoiceStore.update(state => ({
      ...state,
      filters: { ...$InvoiceStore.filters, order: sort.order, direction: sort.direction },
    }));
    await fetchInvoices();
  };

  const handleDistributorsSuccess = (response: { id: number; name: string }[]) => {
    distributors = response
      .filter(d => d && d.name)
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  };

  const handleDistributorsError = (error: any) => {
    const errorMessage =
      error?.response?.data?.message || "Failed to fetch distributors. Please try again.";

    Flash.now({ type: "error", message: errorMessage });
  };

  const loadDistributors = async () => {
    try {
      await PaymentsService.fetchDistributors(response => {
        distributors = response
          .filter(d => d && d.name)
          .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      }, handleDistributorsError);
    } catch (error) {
      handleDistributorsError(error);
    }
  };

  const handleDistributorSearch = e => {
    const searchTerm = e.target.value.toLowerCase().trim();

    if (!searchTerm) {
      loadDistributors();
    } else {
      distributors = distributors.filter(({ name }) => name.toLowerCase().includes(searchTerm));
    }
  };

  const handleDistributorSelect = e => {
    const distributorId = Number(e.target.value);

    if (e.target.checked) {
      selectedDistributors = [...selectedDistributors, distributorId];
    } else {
      selectedDistributors = selectedDistributors.filter(id => id !== distributorId);
    }

    InvoiceStore.update(state => ({
      ...state,
      filters: {
        ...state.filters,
        distributor_ids: selectedDistributors.join(","),
      },
    }));

    fetchInvoices();
  };

  const handleDistributorFilterClear = () => {
    selectedDistributors = [];

    InvoiceStore.update(state => ({
      ...state,
      filters: {
        ...state.filters,
        distributor_ids: "",
      },
    }));

    fetchInvoices();
  };

  // lifecycle
  onMount(() => {
    let i = document.getElementById("retailer-invoice-search-input");
    if (i) i.focus();
    (async () => {
      await SuspensionService.get();
      await UserNotificationsService.getNotifications();
      await InvoiceService.counts();
      await fetchInvoices();
      await loadDistributors();
      await DwollaIdentityService.get();
    })();
  });
  onDestroy(() => {
    resetInvoiceStore();
  });

  // reactives
  $: {
    if ($InvoiceStore.currentTab && $InvoiceStore.currentTab == "outstanding") {
      buttonLabel = "All Transaction Dates";
    } else {
      buttonLabel = "All Paid Dates";
    }
  }

  $: hasInvoices = selectedInvoices.length > 0;
  $: showLoadingState = !$InvoiceStore.loaded;
  $: storesLoaded =
    $InvoiceStore.loaded &&
    $dwollaIdentityStore.loaded &&
    !$UserNotificationsStore.loading &&
    $SuspensionStore.loaded;
  $: searchRan = !!searchValue && $InvoiceStore.loaded;
  $: updateSearchValue($InvoiceStore.currentTab, searchValue);
  $: pageNum = $InvoiceStore.pagination.page;
  $: pagination = {
    ...$InvoiceStore.pagination,
    goToPage: nextPageNum => fetchInvoices(nextPageNum),
  };
</script>

<style>
  .position-pay-selected-button {
    position: absolute;
    right: 16px;
    top: -8px;
  }
</style>

<div data-testid="invoices-index" class="fizz-surface">
  <div class="fizz-expand-32-on-large fizz-expand-16-on-small">
    {#if storesLoaded}
      <InvoicesBannerSelector />
    {/if}

    <div class="fizz-relative">
      <ul class="fizz-tab-list">
        <li
          class="outstanding {$InvoiceStore.currentTab == InvoicesStatuses.OUTSTANDING &&
            'fizz-is-active'}"
          on:click|preventDefault={() => handleTabToggle(InvoicesStatuses.OUTSTANDING)}
        >
          Outstanding ({$InvoiceStore.counts.unpaid})
        </li>
        <li
          class="paid {$InvoiceStore.currentTab == InvoicesStatuses.PAID && 'fizz-is-active'}"
          on:click|preventDefault={() => handleTabToggle(InvoicesStatuses.PAID)}
        >
          Paid
        </li>
      </ul>
      {#if $InvoiceStore.currentTab === "outstanding"}
        <button
          class="pay-invoices fizz-button position-pay-selected-button fizz-inline-12 fizz-hide-on-small"
          on:click|preventDefault={paySelected}
          disabled={!hasInvoices || $userDisabled}
        >
          Pay Selected {#if hasInvoices}({selectedInvoices.length}){/if}
        </button>
      {/if}
    </div>

    <div class="fizz-flex-row-responsive fizz-inset-square-32">
      <div>
        <input
          type="text"
          name="search"
          id="retailer-invoice-search-input"
          placeholder="Search by invoice number"
          class="fizz-input-search fizz-full-width"
          style="min-width: 230px"
          bind:value={searchValue}
          on:keyup={_ => fetchInvoices()}
        />
      </div>
      <div class="grid-row">
        <div class="grid-col c12-c12 fizz-styles">
          <MultiSearchAndSelectDropdown
            minWidth={"20rem"}
            options={distributors}
            defaultButtonLabel="All Distributors"
            object="distributor"
            searchValue={distributorSearchValue}
            onDistributorSearch={handleDistributorSearch}
            onCheckboxChange={handleDistributorSelect}
            onClear={handleDistributorFilterClear}
            bind:selectedValues={selectedDistributors}
            alignmentOptions={{ desktop: { xPosition: "left" }, mobile: { xPosition: "left" } }}
            spacing={"space-y-4"}
            showSelectAll={true}
          />
        </div>
      </div>
      <div>
        <DatePicker
          bind:startDate={$InvoiceStore.filters.startDate}
          bind:endDate={$InvoiceStore.filters.endDate}
          defaultButtonLabel={buttonLabel}
          runFilters={fetchInvoices}
          minWidth={"100%"}
          extraLabelClasses={"fizz-text-strong"}
          selectButtonStyle
          alignmentOptions={{ desktop: { xPosition: "left" }, mobile: { xPosition: "left" } }}
        />
      </div>

      {#if $InvoiceStore.currentTab === "paid"}
        <div>
          <select
            class="fizz-select fizz-full-width fizz-text-strong"
            bind:value={statusFilter}
            on:change={_ => fetchInvoices()}
            data-testid="status-filter"
          >
            {#each statusFilterOptions as { option, value }}
              <option {value}>
                {option}
              </option>
            {/each}
          </select>
        </div>
        <div>
          <select
            class="fizz-select fizz-full-width fizz-text-strong"
            bind:value={$InvoiceStore.filters.type}
            on:change={_ => fetchInvoices()}
            data-testid="invoices-filter"
          >
            {#each invoiceFilterOptions as { option, value }}
              <option {value}>
                {option}
              </option>
            {/each}
          </select>
        </div>
      {/if}

      {#if $InvoiceStore.currentTab === "outstanding"}
        <div class="fizz-inset-square-16 fizz-expand-16-on-small fizz-hide-on-large">
          <button
            class="pay-invoices fizz-button fizz-full-width"
            on:click|preventDefault={paySelected}
            disabled={!hasInvoices || $userDisabled}
          >
            Pay Selected {#if hasInvoices}({selectedInvoices.length}){/if}
          </button>
        </div>
      {/if}
    </div>
    <table
      class="fizz-table fizz-stack-12"
      style="--heading-col-width: 120px"
      class:fizz-table-highlight-rows={$InvoiceStore.invoices.length > 0 && !showLoadingState}
    >
      {#if $InvoiceStore.currentTab === "paid"}
        <table
          class="fizz-table fizz-stack-12"
          class:fizz-table-highlight-rows={$InvoiceStore.invoices.length > 0 && !showLoadingState}
        >
          <PaidInvoiceTable
            invoices={$InvoiceStore.invoices}
            {viewInvoice}
            {searchRan}
            {showLoadingState}
            order={$InvoiceStore.filters.order}
            direction={$InvoiceStore.filters.direction}
            {handleSort}
          />
        </table>
      {:else}
        <table
          class="fizz-table fizz-stack-12 fizz-row-select"
          class:fizz-table-highlight-rows={$InvoiceStore.invoices.length > 0 && !showLoadingState}
        >
          <OutstandingInvoiceTable
            bind:selectedInvoices
            invoices={$InvoiceStore.invoices}
            {viewInvoice}
            {cancelScheduledPayment}
            {handleScheduledPaymentModification}
            {searchRan}
            {showLoadingState}
          />
        </table>
      {/if}
    </table>
    {#if $InvoiceStore.invoices.length > 0}
      <div class="fizz-stack-12">
        <PaginationLarge {pagination} />
      </div>
    {/if}
  </div>
</div>
<PayWithCreditsModal
  {shouldShowPayInvoicesModal}
  bind:scheduledDate
  bind:invoices={selectedInvoices}
  {onSchedulePayment}
  {togglePayInvoicesModal}
/>
